/* ══════════════════════════════════════════════════════════════
 *  DocumentExplorerPage — React migration of FE/Caseview/Explorer
 *
 *  S3 file browser scoped to a single case.  All 26 features from
 *  the legacy explorer are preserved:
 *    browse / breadcrumbs / upload (drag-drop + picker) / download /
 *    download-zip / delete / rename / create folder / view office online /
 *    view PDF/image / view EML/MSG / file-type icons / uploaded-by /
 *    select-all / object count / refresh / permissions (RBAC) / duplicate
 *    detection / progress bars / error handling / sorting (folders first)
 * ══════════════════════════════════════════════════════════════ */
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type DragEvent,
  type ChangeEvent,
} from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  faFolderPlus,
  faCloudUploadAlt,
  faDownload,
  faTrashAlt,
  faEdit,
  faSyncAlt,
  faFolder,
  faFolderOpen,
  faFile,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFilePdf,
  faFileAlt,
  faFileCode,
  faFileImage,
  faFileArchive,
  faEnvelope,
  faTimes,
  faChevronRight,
  faHome,
  faArrowLeft,
  faSortUp,
  faSortDown,
  faSort,
  faCheck,
  faExclamationTriangle,
  faSpinner,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import {
  useListObjects,
  useExplorerPermissions,
  useCreateFolder,
  useDeleteObjects,
  useRenameObject,
  useCategoriseDocuments,
  useCheckDuplicate,
  getSignedUrl,
  createShortUrl,
  downloadZip,
  uploadFileDirectWithRetry,
  getPdfPageCount,
  parseEmail,
  BUCKET,
} from '../../hooks/useExplorer';
import { CONCURRENT_UPLOAD_LIMIT } from '../../config';
import { useDocumentCategories, useMarkForPrinting } from '../../hooks/useAdmin';
import type { S3Object, BreadcrumbItem, ParsedEmail, LedgerEntry } from '../../types/explorer';
import type { ChunkUploadProgress } from '../../hooks/useExplorer';

import s from './ExplorerPage.module.css';

/* ══════════════════════════════════════════════
 *  Constants & Helpers
 * ══════════════════════════════════════════════ */
const OFFICE_EXTS = new Set(['doc', 'docx', 'dotx', 'xls', 'xlsx', 'xltx', 'ppt', 'pptx', 'potx']);

function extOf(key: string): string {
  const dot = key.lastIndexOf('.');
  return dot > -1 ? key.slice(dot + 1).toLowerCase() : '';
}

function isFolder(key: string) {
  return key.endsWith('/');
}

function fileName(key: string) {
  if (isFolder(key)) {
    const parts = key.replace(/\/$/, '').split('/');
    return parts[parts.length - 1] + '/';
  }
  return key.split('/').pop() ?? key;
}

function parentPath(key: string) {
  const parts = key.split('/').filter(Boolean);
  parts.pop();
  return parts.length ? parts.join('/') + '/' : '';
}

function bytesToSize(bytes: number): string {
  if (bytes === 0) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function timeAgo(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const sec = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-GB');
}

function formatTimestamp(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

type FileIconResult = { icon: typeof faFile; color: string };

function getFileIcon(key: string): FileIconResult {
  if (isFolder(key)) return { icon: faFolder, color: '#ffc107' };
  const ext = extOf(key);
  switch (ext) {
    case 'doc':
    case 'docx':
    case 'dotx':
      return { icon: faFileWord, color: '#2b579a' };
    case 'xls':
    case 'xlsx':
    case 'xltx':
      return { icon: faFileExcel, color: '#107c41' };
    case 'ppt':
    case 'pptx':
    case 'potx':
      return { icon: faFilePowerpoint, color: '#d24726' };
    case 'pdf':
      return { icon: faFilePdf, color: '#dc3545' };
    case 'txt':
      return { icon: faFileAlt, color: '#6c757d' };
    case 'html':
    case 'xml':
      return { icon: faFileCode, color: '#fd7e14' };
    case 'msg':
    case 'eml':
      return { icon: faEnvelope, color: 'var(--info-500)' };
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
    case 'tiff':
    case 'tif':
    case 'webp':
      return { icon: faFileImage, color: '#6f42c1' };
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return { icon: faFileArchive, color: '#495057' };
    default:
      return { icon: faFile, color: '#6c757d' };
  }
}

/** Build breadcrumb items from a prefix like "CaseType/CaseRef/subfolder/" */
function buildBreadcrumbs(prefix: string, rootPrefix: string): BreadcrumbItem[] {
  // rootPrefix is e.g. "FSCS/FS-001/" — the part we never go above
  const relative = prefix.startsWith(rootPrefix) ? prefix.slice(rootPrefix.length) : '';
  const crumbs: BreadcrumbItem[] = [{ label: 'Root', prefix: rootPrefix }];
  if (!relative) return crumbs;
  const parts = relative.replace(/\/$/, '').split('/');
  let accum = rootPrefix;
  for (const part of parts) {
    accum += part + '/';
    crumbs.push({ label: part, prefix: accum });
  }
  return crumbs;
}

type SortKey = 'name' | 'folder' | 'modified' | 'size' | 'uploadedBy';
type SortDir = 'asc' | 'desc';

/** Sorted items — folders always before files */
function sortObjects(items: S3Object[], key: SortKey, dir: SortDir): S3Object[] {
  const cmp = (a: S3Object, b: S3Object): number => {
    const aIsFolder = isFolder(a.Key);
    const bIsFolder = isFolder(b.Key);
    if (aIsFolder !== bIsFolder) return aIsFolder ? -1 : 1;

    let va: string | number = '';
    let vb: string | number = '';
    switch (key) {
      case 'name':
        va = fileName(a.Key).toLowerCase();
        vb = fileName(b.Key).toLowerCase();
        break;
      case 'folder':
        va = parentPath(a.Key).toLowerCase();
        vb = parentPath(b.Key).toLowerCase();
        break;
      case 'modified':
        va = a.LastModified ? new Date(a.LastModified).getTime() : 0;
        vb = b.LastModified ? new Date(b.LastModified).getTime() : 0;
        break;
      case 'size':
        va = a.Size ?? 0;
        vb = b.Size ?? 0;
        break;
      case 'uploadedBy':
        va = (a.UploadedBy ?? a.StorageClass ?? '').toLowerCase();
        vb = (b.UploadedBy ?? b.StorageClass ?? '').toLowerCase();
        break;
    }
    if (va < vb) return dir === 'asc' ? -1 : 1;
    if (va > vb) return dir === 'asc' ? 1 : -1;
    return 0;
  };
  return [...items].sort(cmp);
}

/* ══════════════════════════════════════════════
 *  Component
 * ══════════════════════════════════════════════ */
export default function ExplorerPage() {
  const { id: caseRef } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ── S3 prefix navigation ── */
  // CaseFilePage always passes ?prefix= with the fully-resolved S3 path
  const rootPrefix = useMemo(() => {
    const qp = searchParams.get('prefix') || '';
    return qp.endsWith('/') ? qp : qp + '/';
  }, [searchParams]);

  const [prefix, setPrefix] = useState(rootPrefix);
  useEffect(() => {
    setPrefix(rootPrefix);
  }, [rootPrefix]);

  /* ── Data ── */
  const { data: listing, isLoading, refetch } = useListObjects(prefix, { includeLedger: true });
  const { data: perms } = useExplorerPermissions();
  const canDelete = perms?.canDeleteDocuments ?? false;
  const { data: categories = [] } = useDocumentCategories();

  /* ── Ledger mutations ── */
  const categoriseMut = useCategoriseDocuments();
  const markPrintingMut = useMarkForPrinting();
  const checkDuplicate = useCheckDuplicate();

  /* ── Merge contents + common-prefixes into a single item list ── */
  const items = useMemo<S3Object[]>(() => {
    const objs: S3Object[] = [];
    // Folders from CommonPrefixes
    if (listing?.CommonPrefixes) {
      for (const cp of listing.CommonPrefixes) {
        objs.push({ Key: cp.Prefix });
      }
    }
    // Files (skip the prefix-self entry)
    if (listing?.Contents) {
      for (const c of listing.Contents) {
        if (c.Key === prefix) continue; // skip "folder marker" key
        objs.push(c);
      }
    }
    return objs;
  }, [listing, prefix]);

  /* ── Sort ── */
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const sorted = useMemo(() => sortObjects(items, sortKey, sortDir), [items, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  /* ── Search / filter ── */
  const [search, setSearch] = useState('');
  const displayed = useMemo(() => {
    if (!search.trim()) return sorted;
    const q = search.toLowerCase();
    return sorted.filter((o) => fileName(o.Key).toLowerCase().includes(q));
  }, [sorted, search]);

  /* ── Selection ── */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Clear selection on prefix change
  useEffect(() => {
    setSelected(new Set());
  }, [prefix]);

  function toggleSelect(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }
  function toggleSelectAll() {
    if (selected.size === displayed.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(displayed.map((o) => o.Key)));
    }
  }

  /* ── Breadcrumbs ── */
  const breadcrumbs = useMemo(() => buildBreadcrumbs(prefix, rootPrefix), [prefix, rootPrefix]);

  /* ── Navigate into folder ── */
  function navigate(newPrefix: string) {
    setPrefix(newPrefix);
    setSearch('');
  }

  /* ── Modals ── */
  const [folderModal, setFolderModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [renameModal, setRenameModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [renameExt, setRenameExt] = useState('');
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, ChunkUploadProgress>>({});
  const [uploading, setUploading] = useState(false);
  const [uploadCategoryId, setUploadCategoryId] = useState<number | null>(null);
  const [emailModal, setEmailModal] = useState(false);
  const [emailData, setEmailData] = useState<ParsedEmail | null>(null);
  const [emailFileName, setEmailFileName] = useState('');
  const [emailError, setEmailError] = useState('');

  /* ── Drag & Drop state ── */
  const [dragOver, setDragOver] = useState(false);
  const dragCounter = useRef(0);

  /* ── Drag-to-folder state ── */
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dropTargetFolder, setDropTargetFolder] = useState<string | null>(null);
  const [movingItems, setMovingItems] = useState(false);

  /* ── Category context menu ── */
  const [catMenu, setCatMenu] = useState<{ keys: string[]; x: number; y: number } | null>(null);
  const [tagSelectValue, setTagSelectValue] = useState('');

  // Close category menu on outside click
  useEffect(() => {
    if (!catMenu) return;
    const close = () => setCatMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [catMenu]);

  /* ── Mutations ── */
  const createFolderMut = useCreateFolder(prefix);
  const deleteMut = useDeleteObjects(prefix);
  const renameMut = useRenameObject(prefix);

  /* ════════════════════════════════════════════
   *  Action handlers
   * ════════════════════════════════════════════ */

  /* ── Create folder ── */
  function handleCreateFolder() {
    const name = folderName.trim();
    if (!name) return;
    const folderKey = prefix + name + '/';
    createFolderMut.mutate(
      { bucket: BUCKET, folderKey },
      {
        onSuccess: () => {
          showToast(`Folder "${name}" created`, 'success');
          setFolderModal(false);
          setFolderName('');
        },
        onError: () => showToast('Failed to create folder', 'error'),
      },
    );
  }

  /* ── Delete ── */
  function handleDelete() {
    const keys = Array.from(selected);
    if (!keys.length) return;
    deleteMut.mutate(
      { bucket: BUCKET, keys },
      {
        onSuccess: () => {
          showToast(`${keys.length} item(s) deleted`, 'success');
          setDeleteModal(false);
          setSelected(new Set());
        },
        onError: () => showToast('Delete failed', 'error'),
      },
    );
  }

  /* ── Rename ── */
  function openRenameModal() {
    if (selected.size !== 1) return;
    const key = Array.from(selected)[0];
    const name = fileName(key);
    const dot = name.lastIndexOf('.');
    if (dot > 0) {
      setRenameValue(name.slice(0, dot));
      setRenameExt(name.slice(dot));
    } else {
      setRenameValue(name.replace(/\/$/, ''));
      setRenameExt('');
    }
    setRenameTarget(key);
    setRenameModal(true);
  }
  function handleRename() {
    const newName = renameValue.trim();
    if (!newName) return;
    if (newName.includes('/')) {
      showToast('Name cannot contain slashes', 'error');
      return;
    }
    const newKey = parentPath(renameTarget) + newName + renameExt;
    if (newKey === renameTarget) {
      setRenameModal(false);
      return;
    }
    renameMut.mutate(
      { bucket: BUCKET, oldKey: renameTarget, newKey },
      {
        onSuccess: () => {
          showToast('File renamed', 'success');
          setRenameModal(false);
          setSelected(new Set());
        },
        onError: () => showToast('Rename failed', 'error'),
      },
    );
  }

  /* ── Click on a file/folder ── */
  async function handleItemClick(obj: S3Object) {
    const key = obj.Key;
    if (isFolder(key)) {
      navigate(key);
      return;
    }
    const ext = extOf(key);
    // Email files → parse & show modal
    if (ext === 'eml' || ext === 'msg') {
      try {
        setEmailFileName(fileName(key));
        setEmailData(null);
        setEmailError('');
        setEmailModal(true);
        const parsed = await parseEmail({ bucket: BUCKET, key, fileType: ext as 'eml' | 'msg' });
        setEmailData(parsed);
      } catch {
        setEmailError('Failed to parse email. You can download the original file.');
      }
      return;
    }
    // Office docs → short URL → Office Online
    if (OFFICE_EXTS.has(ext)) {
      try {
        const shortUrl = await createShortUrl(key);
        window.open(
          `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(shortUrl)}`,
          '_blank',
        );
      } catch {
        // Fallback: open signed URL directly
        const url = await getSignedUrl(key);
        window.open(url, '_blank');
      }
      return;
    }
    // Everything else → presigned URL in new tab
    try {
      const url = await getSignedUrl(key);
      window.open(url, '_blank');
    } catch {
      showToast('Failed to generate download link', 'error');
    }
  }

  /* ── Download (single or zip) ── */
  async function handleDownload() {
    const keys = Array.from(selected);
    if (!keys.length) return;
    try {
      if (keys.length === 1 && !isFolder(keys[0])) {
        // Single file download
        const url = await getSignedUrl(keys[0], 'attachment');
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName(keys[0]);
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        // Zip download
        const zipName = (caseRef ?? 'download') + '.zip';
        const blob = await downloadZip({ bucket: BUCKET, keys, zipName });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = zipName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
      showToast('Download started', 'success');
    } catch {
      showToast('Download failed', 'error');
    }
  }

  /* ── Upload ── */
  function openUploadFromPicker(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    prepareUpload(files);
    e.target.value = ''; // reset so same file can be re-selected
  }

  const MAX_FILENAME_LENGTH = 200;
  const MAX_FILE_SIZE_BYTES = 250 * 1024 * 1024; // 250 MB

  function prepareUpload(files: File[]) {
    // ── Pre-upload validation ──
    const rejected: { name: string; reason: string }[] = [];
    const valid: File[] = [];
    for (const file of files) {
      if (file.name.length > MAX_FILENAME_LENGTH) {
        rejected.push({
          name: file.name,
          reason: `Filename is too long (${file.name.length} chars, max ${MAX_FILENAME_LENGTH})`,
        });
      } else if (file.size === 0) {
        rejected.push({ name: file.name, reason: 'File is empty (0 bytes)' });
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        rejected.push({
          name: file.name,
          reason: `File exceeds the 250 MB limit (${bytesToSize(file.size)})`,
        });
      } else {
        valid.push(file);
      }
    }
    if (rejected.length > 0) {
      if (rejected.length === 1) {
        showToast(`Cannot upload "${rejected[0].name}": ${rejected[0].reason}`, 'error');
      } else {
        showToast(
          `${rejected.length} files cannot be uploaded (filename too long, empty, or too large). Check the console for details.`,
          'error',
        );
        rejected.forEach((r) => console.warn(`[Upload rejected] ${r.name}: ${r.reason}`));
      }
    }
    if (valid.length === 0) return;

    // Check for duplicates against current listing
    const existingNames = new Set(
      items.filter((o) => !isFolder(o.Key)).map((o) => fileName(o.Key)),
    );
    const dupes = valid.filter((f) => existingNames.has(f.name));
    if (dupes.length > 0) {
      const names = dupes.map((f) => f.name).join(', ');
      if (
        !window.confirm(
          `The following files already exist and will be overwritten:\n\n${names}\n\nContinue?`,
        )
      ) {
        return;
      }
    }
    setUploadFiles(valid);
    setUploadProgress({});
    setUploading(false);
    setUploadModal(true);
  }

  async function executeUpload() {
    if (uploading) return;
    setUploading(true);
    const uploadedBy = user?.username ?? user?.email ?? 'unknown';

    // This page is opened for ONE case (route /cases/:id/explorer), so that case — not a folder name —
    // is what an upload belongs to. The old code took the second folder of the prefix, which for
    // two-level case types ("Plevin/Sandstone-Book/<CaseRef>/") is the book name, not the case.
    // The server also works the case out from the S3 key and ignores a wrong value; this keeps the page honest.
    const pathParts = prefix.split('/').filter(Boolean);
    const caseType = pathParts.length > 0 ? pathParts[0] : 'Unknown'; // folder hint only; the server resolves the real case type
    const extractedCaseRef = caseRef || (pathParts.length > 1 ? pathParts[1] : 'Unknown');

    // Compute SHA-256 hashes and pre-check for duplicates within the same case
    const fileHashes: Record<string, string> = {};
    const duplicates: Array<{ name: string; existingName: string; caseRef: string | null }> = [];
    for (const file of uploadFiles) {
      try {
        const buffer = await file.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
        const hash = Array.from(new Uint8Array(hashBuffer))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
        fileHashes[file.name] = hash;
        const result = await checkDuplicate.mutateAsync({
          contentHash: hash,
          caseRef: extractedCaseRef || undefined,
        });
        if (result.isDuplicate && result.existing.length > 0) {
          duplicates.push({
            name: file.name,
            existingName: result.existing[0].file_name,
            caseRef: result.existing[0].case_ref,
          });
        }
      } catch {
        // Non-fatal — proceed without hash/dedup for this file
      }
    }

    if (duplicates.length > 0) {
      const msg = duplicates
        .map((d) => `• ${d.name} → matches: ${d.existingName}${d.caseRef ? ` (${d.caseRef})` : ''}`)
        .join('\n');
      if (
        !window.confirm(
          `The following files already exist in the document ledger:\n\n${msg}\n\nUpload anyway?`,
        )
      ) {
        setUploading(false);
        return;
      }
    }

    // Concurrent upload queue management
    const queue = [...uploadFiles];
    const inProgress = new Set<Promise<void>>();
    let errorCount = 0;

    while (queue.length > 0 || inProgress.size > 0) {
      // Start new uploads up to concurrent limit
      while (queue.length > 0 && inProgress.size < CONCURRENT_UPLOAD_LIMIT) {
        const file = queue.shift()!;
        const key = prefix + file.name;

        // Extract PDF page count (non-blocking, best-effort)
        const pageCountPromise = getPdfPageCount(file);

        const uploadPromise = (async () => {
          try {
            const pageCount = await pageCountPromise;

            // Upload directly to S3 using presigned URLs
            await uploadFileDirectWithRetry(
              file,
              key,
              extractedCaseRef,
              caseType,
              uploadedBy,
              (p) => {
                setUploadProgress((prev) => ({ ...prev, [file.name]: p }));
              },
              {
                categoryId: uploadCategoryId,
                contentHash: fileHashes[file.name],
                pageCount,
              },
            );
          } catch (err) {
            errorCount++;
            const message = err instanceof Error ? err.message : 'Upload failed';
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: { fileName: file.name, percent: 0, done: true, error: message },
            }));
          }
        })();

        inProgress.add(uploadPromise);

        // Remove from in-progress when done
        uploadPromise.finally(() => {
          inProgress.delete(uploadPromise);
        });
      }

      // Wait for at least one upload to complete before continuing
      if (inProgress.size > 0) {
        await Promise.race(inProgress);
      }
    }

    setUploading(false);
    queryClient.invalidateQueries({ queryKey: ['explorer', 'list', prefix] });
    if (errorCount === 0) {
      showToast(`${uploadFiles.length} file(s) uploaded successfully`, 'success');
      setTimeout(() => {
        setUploadModal(false);
        setUploadCategoryId(null);
      }, 800);
    } else if (errorCount < uploadFiles.length) {
      showToast(
        `${uploadFiles.length - errorCount} of ${uploadFiles.length} files uploaded. ${errorCount} failed — see details above.`,
        'error',
      );
    } else {
      showToast('Upload failed — no files were uploaded. See details above.', 'error');
    }
  }

  /* ── Drag & Drop ── */
  function onDragEnter(e: DragEvent) {
    e.preventDefault();
    dragCounter.current++;
    setDragOver(true);
  }
  function onDragLeave(e: DragEvent) {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragOver(false);
    }
  }
  function onDragOver(e: DragEvent) {
    e.preventDefault();
  }
  const onDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault();
      dragCounter.current = 0;
      setDragOver(false);

      // Ignore internal row drags (handled by folder drop targets)
      if (e.dataTransfer.getData('text/plain') && draggedKey) return;

      const dtItems = e.dataTransfer.items;
      const files: File[] = [];

      // Try to get directory entries for recursive folder support
      if (dtItems) {
        const entries: FileSystemEntry[] = [];
        for (let i = 0; i < dtItems.length; i++) {
          const entry = dtItems[i].webkitGetAsEntry?.();
          if (entry) entries.push(entry);
        }
        if (entries.length > 0) {
          const collected = await collectFiles(entries, '');
          files.push(...collected);
        } else {
          // Fallback: just use files
          for (let i = 0; i < dtItems.length; i++) {
            const f = dtItems[i].getAsFile();
            if (f) files.push(f);
          }
        }
      }
      if (files.length) prepareUpload(files);
    },

    [items, prefix, user, draggedKey],
  );

  /* ── Move items into folder (drag-to-folder) ── */
  async function handleMoveToFolder(targetFolderKey: string) {
    // Determine which keys to move: if the dragged item is in the selection, move all selected; otherwise just the dragged one
    const keysToMove =
      draggedKey && selected.has(draggedKey)
        ? Array.from(selected).filter((k) => k !== targetFolderKey && !isFolder(k))
        : draggedKey
          ? [draggedKey]
          : [];

    if (!keysToMove.length) return;

    setMovingItems(true);
    const folderBase = targetFolderKey; // already has trailing slash
    let successCount = 0;
    for (const oldKey of keysToMove) {
      const name = fileName(oldKey);
      const newKey = folderBase + name;
      if (newKey === oldKey) continue;
      try {
        await renameMut.mutateAsync({ bucket: BUCKET, oldKey, newKey });
        successCount++;
      } catch {
        showToast(`Failed to move "${name}"`, 'error');
      }
    }
    if (successCount > 0) {
      showToast(
        `Moved ${successCount} item(s) to ${fileName(targetFolderKey).replace(/\/$/, '')}`,
        'success',
      );
      queryClient.invalidateQueries({ queryKey: ['explorer', 'list', prefix] });
      setSelected(new Set());
    }
    setMovingItems(false);
    setDraggedKey(null);
    setDropTargetFolder(null);
  }

  /* ── Row-level drag handlers (move within explorer) ── */
  function onRowDragStart(e: DragEvent<HTMLTableRowElement>, key: string) {
    // Don't interfere with external file drags
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
    setDraggedKey(key);
  }

  function onRowDragEnd() {
    setDraggedKey(null);
    setDropTargetFolder(null);
  }

  function onFolderDragEnter(e: DragEvent<HTMLTableRowElement>, folderKey: string) {
    e.preventDefault();
    e.stopPropagation();
    if (draggedKey && folderKey !== draggedKey) {
      setDropTargetFolder(folderKey);
    }
  }

  function onFolderDragOver(e: DragEvent<HTMLTableRowElement>) {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  }

  function onFolderDragLeave(e: DragEvent<HTMLTableRowElement>) {
    e.preventDefault();
    e.stopPropagation();
    // Only clear if we're actually leaving the row (not entering a child element)
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDropTargetFolder(null);
    }
  }

  function onFolderDrop(e: DragEvent<HTMLTableRowElement>, folderKey: string) {
    e.preventDefault();
    e.stopPropagation();
    // Only handle internal drags (not external file drops)
    if (draggedKey) {
      handleMoveToFolder(folderKey);
    }
    setDropTargetFolder(null);
  }

  /* ── Refresh ── */
  function handleRefresh() {
    refetch();
  }

  /* ── Categorise files ── */
  async function handleCategorise(keys: string[], categoryId: number | null) {
    try {
      await categoriseMut.mutateAsync({ s3Keys: keys, categoryId });
      showToast(categoryId ? 'Category assigned' : 'Category cleared', 'success');
      refetch();
    } catch {
      showToast('Failed to update category', 'error');
    }
  }

  /* ── Tag selected files ── */
  async function handleTagSelected() {
    if (!tagSelectValue || selected.size === 0) return;
    const keys = Array.from(selected).filter((k) => !isFolder(k));
    if (keys.length === 0) return;
    const categoryId = tagSelectValue === 'clear' ? null : Number(tagSelectValue);
    try {
      await categoriseMut.mutateAsync({ s3Keys: keys, categoryId });
      showToast(`Tagged ${keys.length} file(s)`, 'success');
      setTagSelectValue('');
      refetch();
    } catch {
      showToast('Failed to tag files', 'error');
    }
  }

  /* ── Toggle mark for printing ── */
  async function handleMarkForPrinting(documentId: string | number, mark: boolean) {
    try {
      await markPrintingMut.mutateAsync({ id: documentId, mark });
      refetch();
    } catch {
      showToast('Failed to update print flag', 'error');
    }
  }

  /* ── Print queue download ── */
  async function handlePrintQueue() {
    const printKeys = items
      .filter((obj) => !isFolder(obj.Key) && listing?.ledgerData?.[obj.Key]?.markForPrinting)
      .map((obj) => obj.Key);
    if (printKeys.length === 0) {
      showToast('No documents marked for printing', 'info');
      return;
    }
    try {
      const zipName = `${caseRef ?? 'case'}_print_queue.zip`;
      const blob = await downloadZip({ bucket: BUCKET, keys: printKeys, zipName });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast(`Downloading ${printKeys.length} document(s) for printing`, 'success');
    } catch {
      showToast('Download failed', 'error');
    }
  }

  /* ── Badge text ── */
  const badgeText = useMemo(() => {
    if (selected.size > 0) return `${selected.size} of ${items.length}`;
    return `${items.length}`;
  }, [selected.size, items.length]);

  /* ══════════════════════════════════════════════
   *  Render
   * ══════════════════════════════════════════════ */
  return (
    <div
      className={`${s.container} ${dragOver ? s.dragOver : ''}`}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* ── Header ── */}
      <div className={s.header}>
        <div className={s.headerLeft}>
          <Link to={`/cases/${caseRef}`} className={s.backLink}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Case</span>
          </Link>
          <h1 className={s.title}>
            <FontAwesomeIcon icon={faFolderOpen} className={s.titleIcon} />
            Document Explorer
          </h1>
          <span className={s.caseRefBadge}>{caseRef}</span>
        </div>
        <div className={s.headerRight}>
          <span className={s.badge}>{badgeText} items</span>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className={s.toolbar}>
        <div className={s.toolbarLeft}>
          {/* Breadcrumbs */}
          <nav className={s.breadcrumbs}>
            {breadcrumbs.map((bc, i) => (
              <span key={bc.prefix} className={s.breadcrumbItem}>
                {i > 0 && <FontAwesomeIcon icon={faChevronRight} className={s.breadcrumbSep} />}
                {i === breadcrumbs.length - 1 ? (
                  <span className={s.breadcrumbCurrent}>{bc.label}</span>
                ) : (
                  <button className={s.breadcrumbLink} onClick={() => navigate(bc.prefix)}>
                    {i === 0 ? <FontAwesomeIcon icon={faHome} /> : bc.label}
                  </button>
                )}
              </span>
            ))}
          </nav>
        </div>
        <div className={s.toolbarRight}>
          {/* Search */}
          <div className={s.searchBox}>
            <FontAwesomeIcon icon={faSearch} className={s.searchIcon} />
            <input
              className={s.searchInput}
              placeholder="Filter files…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {/* Action buttons */}
          <button
            className={s.toolBtn}
            title="New Folder"
            onClick={() => {
              setFolderName('');
              setFolderModal(true);
            }}
          >
            <FontAwesomeIcon icon={faFolderPlus} />
          </button>
          <button
            className={s.toolBtn}
            title="Upload Files"
            onClick={() => fileInputRef.current?.click()}
          >
            <FontAwesomeIcon icon={faCloudUploadAlt} />
          </button>
          <button
            className={s.toolBtn}
            title="Download"
            disabled={selected.size === 0}
            onClick={handleDownload}
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            className={`${s.toolBtn} ${s.toolBtnDanger}`}
            title={
              !canDelete
                ? 'No delete permission'
                : selected.size === 0
                  ? 'Select items to delete'
                  : 'Delete'
            }
            disabled={!canDelete || selected.size === 0}
            onClick={() => setDeleteModal(true)}
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </button>
          <button
            className={s.toolBtn}
            title="Rename"
            disabled={selected.size !== 1}
            onClick={openRenameModal}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          {/* Tag selected */}
          {selected.size > 0 && categories.length > 0 && (
            <>
              <select
                className={s.tagSelect}
                value={tagSelectValue}
                onChange={(e) => setTagSelectValue(e.target.value)}
                title="Assign category to selected files"
              >
                <option value="">🏷 Tag selected…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
                <option value="clear">Clear category</option>
              </select>
              {tagSelectValue && (
                <button
                  className={s.toolBtn}
                  title={`Tag ${selected.size} selected item(s)`}
                  onClick={handleTagSelected}
                  disabled={categoriseMut.isPending}
                >
                  🏷
                </button>
              )}
            </>
          )}
          {/* Print queue */}
          <button className={s.toolBtn} title="Download print queue" onClick={handlePrintQueue}>
            🖨️
          </button>
          <button className={s.toolBtn} title="Refresh" onClick={handleRefresh}>
            <FontAwesomeIcon icon={faSyncAlt} spin={isLoading} />
          </button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={openUploadFromPicker}
      />

      {/* ── Drag overlay ── */}
      {dragOver && !draggedKey && (
        <div className={s.dragOverlay}>
          <FontAwesomeIcon icon={faCloudUploadAlt} className={s.dragIcon} />
          <p>Drop files here to upload</p>
        </div>
      )}

      {/* ── Moving items overlay ── */}
      {movingItems && (
        <div className={s.dragOverlay}>
          <FontAwesomeIcon icon={faSpinner} spin className={s.dragIcon} />
          <p>Moving files…</p>
        </div>
      )}

      {/* ── Table ── */}
      <div className={s.tableWrap}>
        <table className={s.table}>
          <colgroup>
            <col style={{ width: '36px' }} />
            <col style={{ width: '40%' }} />
            <col style={{ width: '110px' }} />
            <col />
            <col />
            <col style={{ width: '36px' }} />
            <col style={{ width: '80px' }} />
          </colgroup>
          <thead>
            <tr>
              <th className={s.thCheck}>
                <input
                  type="checkbox"
                  checked={displayed.length > 0 && selected.size === displayed.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <SortTh
                label="Name"
                sortKey="name"
                current={sortKey}
                dir={sortDir}
                onSort={handleSort}
              />
              <th
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-secondary)',
                  fontWeight: 600,
                }}
              >
                Category
              </th>
              <SortTh
                label="Last Modified"
                sortKey="modified"
                current={sortKey}
                dir={sortDir}
                onSort={handleSort}
              />
              <SortTh
                label="Uploaded By"
                sortKey="uploadedBy"
                current={sortKey}
                dir={sortDir}
                onSort={handleSort}
              />
              <th
                title="Mark for printing"
                style={{ textAlign: 'center', fontSize: 'var(--font-size-sm)' }}
              >
                🖨
              </th>
              <SortTh
                label="Size"
                sortKey="size"
                current={sortKey}
                dir={sortDir}
                onSort={handleSort}
              />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className={s.emptyRow}>
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading…
                </td>
              </tr>
            )}
            {!isLoading && displayed.length === 0 && (
              <tr>
                <td colSpan={7} className={s.emptyRow}>
                  {search ? 'No files match your filter.' : 'This folder is empty.'}
                </td>
              </tr>
            )}
            {displayed.map((obj) => {
              const icon = getFileIcon(obj.Key);
              const name = fileName(obj.Key);
              const isSel = selected.has(obj.Key);
              const objIsFolder = isFolder(obj.Key);
              const isDragTarget = objIsFolder && dropTargetFolder === obj.Key;
              const isDragging = draggedKey === obj.Key;
              const ledger: LedgerEntry | undefined = !objIsFolder
                ? listing?.ledgerData?.[obj.Key]
                : undefined;
              return (
                <tr
                  key={obj.Key}
                  className={`${s.row} ${isSel ? s.rowSelected : ''} ${isDragTarget ? s.rowDropTarget : ''} ${isDragging ? s.rowDragging : ''}`}
                  onClick={() => toggleSelect(obj.Key)}
                  onDoubleClick={() => handleItemClick(obj)}
                  onContextMenu={
                    !objIsFolder
                      ? (e) => {
                          e.preventDefault();
                          setCatMenu({ keys: [obj.Key], x: e.clientX, y: e.clientY });
                        }
                      : undefined
                  }
                  draggable={!objIsFolder}
                  onDragStart={!objIsFolder ? (e) => onRowDragStart(e, obj.Key) : undefined}
                  onDragEnd={!objIsFolder ? onRowDragEnd : undefined}
                  {...(objIsFolder
                    ? {
                        onDragEnter: (e: DragEvent<HTMLTableRowElement>) =>
                          onFolderDragEnter(e, obj.Key),
                        onDragOver: onFolderDragOver,
                        onDragLeave: onFolderDragLeave,
                        onDrop: (e: DragEvent<HTMLTableRowElement>) => onFolderDrop(e, obj.Key),
                      }
                    : {})}
                >
                  <td className={s.tdCheck} onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={isSel} onChange={() => toggleSelect(obj.Key)} />
                  </td>
                  <td className={s.tdName}>
                    <button
                      className={s.fileLink}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleItemClick(obj);
                      }}
                    >
                      <FontAwesomeIcon
                        icon={icon.icon}
                        style={{ color: icon.color }}
                        className={s.fileIcon}
                      />
                      <span>{name}</span>
                    </button>
                  </td>
                  <td className={s.tdMeta}>
                    {!objIsFolder && ledger?.categoryName ? (
                      <span
                        className={s.catBadge}
                        style={{ background: ledger.categoryColor || '#e5e7eb' }}
                      >
                        {ledger.categoryName}
                      </span>
                    ) : !objIsFolder ? (
                      <span className={s.noCategory}>—</span>
                    ) : null}
                  </td>
                  <td className={s.tdMeta}>
                    {obj.LastModified ? (
                      <span title={formatTimestamp(obj.LastModified)}>
                        {timeAgo(obj.LastModified)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={s.tdMeta}>
                    {obj.UploadedBy && obj.UploadedBy !== 'STANDARD' ? (
                      <span className={s.tdUploadedByUser}>{obj.UploadedBy}</span>
                    ) : (
                      <span className={s.tdSystemLabel}>System / Bulk Upload</span>
                    )}
                  </td>
                  <td className={s.tdPrint} onClick={(e) => e.stopPropagation()}>
                    {!objIsFolder && ledger?.documentId != null ? (
                      <input
                        type="checkbox"
                        checked={!!ledger.markForPrinting}
                        onChange={() =>
                          handleMarkForPrinting(ledger.documentId!, !ledger.markForPrinting)
                        }
                        title="Mark for printing"
                      />
                    ) : null}
                  </td>
                  <td className={s.tdSize}>
                    {obj.Size != null && !isFolder(obj.Key) ? bytesToSize(obj.Size) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Category context menu ── */}
      {catMenu && (
        <div
          className={s.catMenu}
          style={{ top: catMenu.y, left: catMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={s.catMenuItem}
              onClick={() => {
                handleCategorise(catMenu.keys, cat.id);
                setCatMenu(null);
              }}
            >
              <span className={s.catSwatch} style={{ background: cat.colour || '#e5e7eb' }} />
              {cat.name}
            </button>
          ))}
          {categories.length > 0 && (
            <hr
              style={{
                margin: '4px 0',
                border: 'none',
                borderTop: '1px solid var(--border-primary)',
              }}
            />
          )}
          <button
            className={s.catMenuItem}
            onClick={() => {
              handleCategorise(catMenu.keys, null);
              setCatMenu(null);
            }}
          >
            Clear Category
          </button>
        </div>
      )}

      {/* ══════════ MODALS ══════════ */}

      {/* ── Create Folder Modal ── */}
      {folderModal && (
        <ModalOverlay title="New Folder" onClose={() => setFolderModal(false)}>
          <div className={s.modalField}>
            <label>Folder name</label>
            <input
              className={s.modalInput}
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="e.g. Correspondence"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
          </div>
          <p className={s.modalHint}>
            Current path: <code>{prefix}</code>
          </p>
          <div className={s.modalActions}>
            <button className={s.btnSecondary} onClick={() => setFolderModal(false)}>
              Cancel
            </button>
            <button
              className={s.btnPrimary}
              onClick={handleCreateFolder}
              disabled={!folderName.trim() || createFolderMut.isPending}
            >
              {createFolderMut.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteModal && (
        <ModalOverlay title="Delete Files" onClose={() => setDeleteModal(false)}>
          <p className={s.deleteWarning}>
            <FontAwesomeIcon icon={faExclamationTriangle} /> Are you sure you want to delete{' '}
            {selected.size} item(s)?
          </p>
          <div className={s.deleteList}>
            {Array.from(selected).map((key) => (
              <div key={key} className={s.deleteItem}>
                <FontAwesomeIcon
                  icon={getFileIcon(key).icon}
                  style={{ color: getFileIcon(key).color }}
                />
                <span>{fileName(key)}</span>
              </div>
            ))}
          </div>
          <div className={s.modalActions}>
            <button className={s.btnSecondary} onClick={() => setDeleteModal(false)}>
              Cancel
            </button>
            <button className={s.btnDanger} onClick={handleDelete} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* ── Rename Modal ── */}
      {renameModal && (
        <ModalOverlay title="Rename File" onClose={() => setRenameModal(false)}>
          <div className={s.modalField}>
            <label>New name</label>
            <div className={s.renameInputWrap}>
              <input
                className={s.modalInput}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleRename()}
              />
              {renameExt && <span className={s.renameExt}>{renameExt}</span>}
            </div>
          </div>
          <ul className={s.renameRules}>
            <li>No slashes (/) allowed</li>
            <li>No trailing spaces</li>
            <li>Extension is preserved automatically</li>
          </ul>
          <div className={s.modalActions}>
            <button className={s.btnSecondary} onClick={() => setRenameModal(false)}>
              Cancel
            </button>
            <button
              className={s.btnPrimary}
              onClick={handleRename}
              disabled={!renameValue.trim() || renameMut.isPending}
            >
              {renameMut.isPending ? 'Renaming…' : 'Rename'}
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* ── Upload Modal ── */}
      {uploadModal && (
        <ModalOverlay
          title="Upload Files"
          onClose={() => {
            if (!uploading) {
              setUploadModal(false);
              setUploadCategoryId(null);
            }
          }}
          wide
        >
          <div className={s.uploadList}>
            {uploadFiles.map((file) => {
              const prog = uploadProgress[file.name];
              return (
                <div key={file.name} className={s.uploadItem}>
                  <div className={s.uploadFileName}>
                    <FontAwesomeIcon
                      icon={getFileIcon(file.name).icon}
                      style={{ color: getFileIcon(file.name).color }}
                    />
                    <span>{file.name}</span>
                    <span className={s.uploadFileSize}>{bytesToSize(file.size)}</span>
                  </div>
                  <div className={s.progressBarOuter}>
                    <div
                      className={`${s.progressBar} ${prog?.error ? s.progressBarError : ''} ${prog?.done && !prog?.error ? s.progressBarDone : ''}`}
                      style={{ width: `${prog?.percent ?? 0}%` }}
                    />
                  </div>
                  {prog?.error && <span className={s.uploadError}>{prog.error}</span>}
                  {prog?.done && !prog?.error && (
                    <span className={s.uploadDone}>
                      <FontAwesomeIcon icon={faCheck} /> Done
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {categories.length > 0 &&
            !uploading &&
            !Object.values(uploadProgress).some((p) => p.done) && (
              <div style={{ marginTop: 12 }}>
                <label
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  Document Category
                  <select
                    value={uploadCategoryId ?? ''}
                    onChange={(e) =>
                      setUploadCategoryId(e.target.value ? Number(e.target.value) : null)
                    }
                    style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      border: '1px solid var(--border-primary)',
                      fontSize: 'var(--font-size-xs)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option value="">Uncategorised</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          <div className={s.modalActions}>
            <button
              className={s.btnSecondary}
              onClick={() => {
                if (!uploading) {
                  setUploadModal(false);
                  setUploadCategoryId(null);
                }
              }}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : 'Cancel'}
            </button>
            {!uploading && !Object.values(uploadProgress).some((p) => p.done) && (
              <button className={s.btnPrimary} onClick={executeUpload}>
                Upload {uploadFiles.length} file(s)
              </button>
            )}
          </div>
        </ModalOverlay>
      )}

      {/* ── Email Viewer Modal ── */}
      {emailModal && (
        <ModalOverlay title={`Email: ${emailFileName}`} onClose={() => setEmailModal(false)} wide>
          {!emailData && !emailError && (
            <div className={s.emailLoading}>
              <FontAwesomeIcon icon={faSpinner} spin /> Parsing…
            </div>
          )}
          {emailError && (
            <div className={s.emailError}>
              <p>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {emailError}
              </p>
              <button
                className={s.btnPrimary}
                onClick={async () => {
                  // Find the key from current items
                  const key = items.find((o) => fileName(o.Key) === emailFileName)?.Key;
                  if (key) {
                    const url = await getSignedUrl(key, 'attachment');
                    window.open(url, '_blank');
                  }
                }}
              >
                Download Original
              </button>
            </div>
          )}
          {emailData && (
            <div className={s.emailContent}>
              <div className={s.emailHeaders}>
                {emailData.from && (
                  <div className={s.emailHeaderRow}>
                    <strong>From:</strong> {emailData.from}
                  </div>
                )}
                {emailData.to && (
                  <div className={s.emailHeaderRow}>
                    <strong>To:</strong> {emailData.to}
                  </div>
                )}
                {emailData.cc && (
                  <div className={s.emailHeaderRow}>
                    <strong>CC:</strong> {emailData.cc}
                  </div>
                )}
                {emailData.bcc && (
                  <div className={s.emailHeaderRow}>
                    <strong>BCC:</strong> {emailData.bcc}
                  </div>
                )}
                {emailData.subject && (
                  <div className={s.emailHeaderRow}>
                    <strong>Subject:</strong> {emailData.subject}
                  </div>
                )}
                {emailData.date && (
                  <div className={s.emailHeaderRow}>
                    <strong>Date:</strong> {emailData.date}
                  </div>
                )}
              </div>
              <div className={s.emailBody}>
                {emailData.htmlBody ? (
                  <iframe
                    srcDoc={emailData.htmlBody}
                    className={s.emailIframe}
                    sandbox="allow-same-origin"
                    title="Email body"
                  />
                ) : (
                  <pre className={s.emailText}>{emailData.textBody ?? '(No body)'}</pre>
                )}
              </div>
              {emailData.attachments && emailData.attachments.length > 0 && (
                <div className={s.emailAttachments}>
                  <h4>Attachments ({emailData.attachments.length})</h4>
                  {emailData.attachments.map((att, i) => (
                    <div key={i} className={s.emailAtt}>
                      <FontAwesomeIcon
                        icon={getFileIcon(att.filename).icon}
                        style={{ color: getFileIcon(att.filename).color }}
                      />
                      <span>{att.filename}</span>
                      {att.size != null && (
                        <span className={s.emailAttSize}>{bytesToSize(att.size)}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {emailData.parseMethod && (
                <div className={s.emailParseMethod}>Parsed via: {emailData.parseMethod}</div>
              )}
            </div>
          )}
        </ModalOverlay>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
 *  Small Sub-Components
 * ══════════════════════════════════════════════ */

/** Sortable table header */
function SortTh({
  label,
  sortKey,
  current,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
}) {
  const isActive = current === sortKey;
  return (
    <th className={s.thSort} onClick={() => onSort(sortKey)}>
      <span>{label}</span>
      <FontAwesomeIcon
        icon={isActive ? (dir === 'asc' ? faSortUp : faSortDown) : faSort}
        className={`${s.sortIcon} ${isActive ? s.sortActive : ''}`}
      />
    </th>
  );
}

/** Reusable modal overlay */
function ModalOverlay({
  children,
  title,
  onClose,
  wide,
}: {
  children: React.ReactNode;
  title: string;
  onClose: () => void;
  wide?: boolean;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  return (
    <div
      className={s.overlay}
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className={`${s.modal} ${wide ? s.modalWide : ''}`}>
        <div className={s.modalHeader}>
          <h3>{title}</h3>
          <button className={s.modalClose} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className={s.modalBody}>{children}</div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
 *  Drag-and-drop directory traversal helper
 * ══════════════════════════════════════════════ */
async function collectFiles(entries: FileSystemEntry[], basePath: string): Promise<File[]> {
  const files: File[] = [];
  for (const entry of entries) {
    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry;
      const file: File = await new Promise((resolve) => fileEntry.file(resolve));
      // Attach relative path for nested folder uploads
      Object.defineProperty(file, 'webkitRelativePath', {
        value: basePath + file.name,
        writable: false,
      });
      files.push(file);
    } else if (entry.isDirectory) {
      const dirEntry = entry as FileSystemDirectoryEntry;
      // FileSystemDirectoryEntry has createDirectoryReader at runtime even if not in TypeScript types
      const reader = (
        dirEntry as unknown as { createDirectoryReader(): FileSystemDirectoryReader }
      ).createDirectoryReader();
      const subEntries: FileSystemEntry[] = await new Promise((resolve) =>
        reader.readEntries(resolve),
      );
      const subFiles = await collectFiles(subEntries, basePath + entry.name + '/');
      files.push(...subFiles);
    }
  }
  return files;
}
